"use client"

import { useEffect, useState } from 'react';
import Cookies from "cookies-js";
import Image from 'next/image';
import { cn } from '@/src/utils/shadcn';
import { Container } from '@/src/components/container';
import { CustomLink } from '@/src/components/custom-link';
import { FaRegFolderOpen } from 'react-icons/fa6';
import axiosConfig from '@/src/utils/axiosConfig.ts';

type Lang = 'ar' | 'en';

interface CategoryItem {
  Id: string;
  Name: string;
  NameAr?: string;
}

interface NewsListItem {
  Id: string;
  Images?: { ImageUrl: string; Title?: string }[];
  CategoryName?: string;
  CategoryNameAr?: string;
  CreatedAt?: string;
  Title?: string;
  TitleAr?: string;
  Content?: string;
  ContentAr?: string;
}

function CategoryList({ category, selectCategory, setSelectCategory, isLoading, isArabic }: {
  category: CategoryItem[];
  selectCategory: string;
  setSelectCategory: (id: string) => void;
  isLoading: boolean;
  isArabic: boolean;
}) {
  return (
    <div className="space-y-5 rounded-5 bg-accent-100 p-8 dark:bg-accent-700 lg:p-10 shadow-md">
      <h3 className="font-secondary text-md font-bold leading-[1.25] text-accent-900 dark:text-white md:text-lg">
        {isArabic ? "الاقسام" : "Category"}
      </h3>

      {isLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-2.5 pt-2.5">
              <div className="h-3 w-3 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
              <div className="h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      ) : category?.length > 0 ? (
        <nav aria-label="category links navigation">
          <ul className="grid gap-2.5 divide-y divide-accent-700/20 dark:divide-accent-200/20">
            <li
              className="flex items-center gap-2.5 pt-2.5 first:pt-0 cursor-pointer"
              onClick={() => setSelectCategory('')}
            >
              <span className={`grid h-3 w-3 place-items-center border ${selectCategory === '' ? "bg-blue-500 border-blue-500" : "border-gray-400 bg-white"}`}>
                {selectCategory === '' && <span className="block h-1.5 w-1.5 bg-white"></span>}
              </span>
              <span className={`text-accent-800 dark:text-gray-300 ${selectCategory === '' ? 'text-blue-600 font-semibold' : ''}`}>
                {isArabic ? "الكل" : "All"}
              </span>
            </li>
            {category.map((cat: CategoryItem) => (
              <li
                key={cat.Id}
                className="flex items-center gap-2.5 pt-2.5 first:pt-0 cursor-pointer"
                onClick={() => setSelectCategory(cat.Id)}
              >
                <span className={`grid h-3 w-3 place-items-center border ${cat.Id === selectCategory ? "bg-blue-500 border-blue-500" : "border-gray-400 bg-white"}`}>
                  {cat.Id === selectCategory && <span className="block h-1.5 w-1.5 bg-white"></span>}
                </span>
                <span className={`text-accent-800 dark:text-gray-300 ${cat.Id === selectCategory ? 'text-blue-600 font-semibold' : ''}`}>
                  {isArabic ? cat.NameAr || cat.Name : cat.Name}
                </span>
              </li>
            ))}
          </ul>
        </nav>
      ) : (
        <p className="text-gray-500 dark:text-gray-400">
          {isArabic ? "لا توجد أقسام متاحة" : "No categories available"}
        </p>
      )}
    </div>
  );
}

export function NewsListSection() {
  const [news, setNews] = useState<NewsListItem[]>([]);
  const [category, setCategory] = useState<CategoryItem[]>([]);
  const [selectCategory, setSelectCategory] = useState<string>('');
  const [isArabic, setIsArabic] = useState<boolean>(false);
  const [lang, setLang] = useState<Lang | null>(null);
  const [loading, setLoading] = useState<{ news: boolean; category: boolean }>({
    news: true,
    category: true
  });
  const [error, setError] = useState<{ news: string | null; category: string | null }>({
    news: null,
    category: null
  });

  useEffect(() => {
    const getLanguage = () => {
      if (typeof window !== "undefined") {
        const pathLang = window.location.pathname.split("/")[1];
        const cookieLang = Cookies.get("lang");
        const newLang: Lang = pathLang === 'ar' ? 'ar' : cookieLang === 'ar' ? 'ar' : 'en';
        setLang(newLang);
        setIsArabic(newLang === 'ar');
        Cookies.set("lang", newLang);
      }
    };

    getLanguage();

    // Listen for route changes
    const handleRouteChange = () => {
      getLanguage();
      // Refresh data when language changes
      fetchData();
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  const fetchDataCategory = async () => {
    if (lang == null) return;
    try {
      setLoading(prev => ({ ...prev, category: true }));
      const categoryRes = await axiosConfig.get(`/api/Category?Language=${lang}`);
      setCategory(categoryRes.data?.Data || []);
      setError(prev => ({ ...prev, category: null }));
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(prev => ({ ...prev, category: isArabic ? 'فشل تحميل الأقسام' : 'Failed to load categories' }));
    } finally {
      setLoading(prev => ({ ...prev, category: false }));
    }
  }

  const fetchData = async () => {
    if (lang == null) return;
    try {
      setLoading(prev => ({ ...prev, news: true }));
      const url = selectCategory
        ? `/api/NewsItem/by-category?CategoryId=${selectCategory}&Language=${lang}`
        : `/api/NewsItem?Language=${lang}`;

      const newsRes = await axiosConfig.get(url);
      setNews((newsRes.data?.Data as NewsListItem[]) || []);
      setError(prev => ({ ...prev, news: null }));
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(prev => ({ ...prev, news: isArabic ? 'لا توجد اخبار' : 'There is no news' }));
    } finally {
      setLoading(prev => ({ ...prev, news: false }));
    }
  };

  useEffect(() => {
    setTimeout(() => {
      fetchDataCategory();
    }, 200);
  }, [lang])

  useEffect(() => {
    setTimeout(() => {
      fetchData();
    }, 200);
  }, [selectCategory, lang]);

  return (
    <section className="section-padding-primary">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[1fr_350px]">
          <div>
            {loading.news ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="rounded-full border animate-pulse">
                  <Image
                    src={"/assets/images/logo/logo.webp"}
                    width={150}
                    height={100}
                    alt="logo"
                    className="rounded-full"
                  />
                </div>
              </div>
            ) : error.news ? (
              <div className="text-center py-10">
                <p className="text-red-500 mb-4">{error.news}</p>
                <button
                  onClick={fetchData}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
                >
                  {isArabic ? 'إعادة المحاولة' : 'Retry'}
                </button>
              </div>
            ) : news.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                {news.map((item: NewsListItem) => (
                  <article key={item.Id} className="group rounded-lg overflow-hidden transition-all duration-300">
                    <CustomLink
                      href={`/${lang}/news/${item.Id}`}
                      className={cn(
                        'relative z-1 overflow-hidden rounded-t-lg',
                        'after:pointer-events-none after:absolute after:left-0 after:top-0 after:z-1 after:h-0 after:w-full after:bg-white/30 after:opacity-100',
                        'group-hover:transition-all group-hover:after:h-full group-hover:after:opacity-0 group-hover:after:duration-400 group-hover:after:ease-linear'
                      )}
                    >
                      {item?.Images?.[0]?.ImageUrl ? (
                        <>
                          <Image
                            src={item.Images[0].ImageUrl}
                            alt={item.Images[0].Title || 'Image'}
                            width={400}
                            height={250}
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                            className="object-cover shadow-md w-full h-[150px] transition-transform duration-500 group-hover:[transform:scale(1)]"
                          />
                          <span className="absolute bottom-0 left-0 bg-primary text-white text-xs px-3 py-1 rounded-tr-lg z-20">
                            {isArabic ? item?.CategoryNameAr || item?.CategoryName : item?.CategoryName}
                          </span>
                        </>
                      ) : (
                        <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <FaRegFolderOpen className="text-4xl text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                    </CustomLink>

                    <div className="pt-2 pb-3 px-3">
                      <div className='flex items-center'>
                        <div className="w-[16px] h-[16px] dark:[filter:brightness(0)_saturate(100%)_invert(42%)_sepia(37%)_saturate(604%)_hue-rotate(184deg)_brightness(91%)_contrast(93%)]">
                          <Image src="/assets/images/icons/date.png" width={16} height={16} alt="date" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-200 text-sm mx-2">
                          {item?.CreatedAt?.split("T")[0]}
                        </p>
                      </div>
                      <h3 className="font-secondary text-sm font-bold leading-[1.5] text-accent-900 dark:text-white md:text-sm mb-3">
                        <CustomLink
                          href={`/${lang}/news/${item.Id}`}
                          className={cn(
                            'text-accent-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-600'
                          )}
                        >
                          {isArabic ? item.TitleAr || item.Title : item.Title || 'Untitled'}
                        </CustomLink>
                      </h3>
                      <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 mb-4">
                        {extractEditorContent((isArabic ? item.ContentAr ?? item.Content : item.Content) ?? '')}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div>
                <Image
                  src="/assets/images/news/no-data.png"
                  alt="No news available"
                  width={250}
                  height={250}
                  className="mx-auto my-5 w-[150px] h-[150px] transition-transform duration-500"
                />
                <p className='text-primary col-span-full text-center'>
                  {isArabic ? 'لا توجد أخبار متاحة' : 'No news available'}
                </p>
                <button
                  onClick={fetchData}
                  className="my-3 mx-auto flex justify-center items-center gap-2 border border-primary px-6 py-1 rounded-full text-xs font-medium text-primary 
                    transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-primary hover:text-white 
                    active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                >
                  <span className="relative">
                    {isArabic ? 'إعادة المحاولة' : 'Retry'}
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className="grid gap-8 self-baseline max-md:mx-auto max-md:max-w-[410px] lg:gap-10 sticky top-[100px]">
            <CategoryList
              category={category}
              selectCategory={selectCategory}
              setSelectCategory={setSelectCategory}
              isLoading={loading.category}
              isArabic={isArabic}
            />

            {error.category && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-600 dark:text-red-400">
                {error.category}
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}

// Keep the extractEditorContent function as is
function extractEditorContent(jsonString: string, wordLimit: number = 14): string {
  try {
    const content = JSON.parse(jsonString);
    if (!content?.blocks) return '';

    const fullText = (content.blocks as Array<{ type: string; data?: { text?: string } }>)
      .filter((block) => block.type === 'paragraph' && !!block.data && typeof block.data.text === 'string')
      .map((block) =>
        (block.data!.text as string)
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
      )
      .join(' ');

    const words = fullText.split(/\s+/).slice(0, wordLimit);
    return words.join(' ') + (words.length >= wordLimit ? '...' : '');
  } catch (error) {
    console.error('Invalid JSON:', error);
    return '';
  }
}